// Pure format recognition. No account matching or writes happen in the browser.
export const cleanCell = value => String(value ?? '').replace(/^\uFEFF/, '').trim();
const normalized = value => cleanCell(value).toLocaleLowerCase('sv').replace(/[_-]/g, ' ').replace(/\s+/g, ' ');
export const isPrivateColumn = value => {
  const h = normalized(value);
  if (/personnr|personnummer|pers\s*nr|samordningsnummer|social security|national id|faktura|allerg|funktionsneds|postnr|postnummer|användar|inloggad|skapad/.test(h)) return true;
  if (/målsman.*(födelse|senast)|födelsedatum.*målsman/.test(h)) return true;
  // Email address is a contact field, not a postal address.
  return /adress|address/.test(h) && !/e post|epost|e mail|email|mailadress|mailaddress/.test(h);
};
export const isSvenskalagHeader = headers => {
  const h = headers.map(cleanCell);
  return ['Förnamn', 'Efternamn'].every(v => h.includes(v))
    && (h.includes('Spelare i') || (h.includes('Medlemstyp') && h.includes('Målsman 1 - Namn')));
};
export function locateHeader(matrix, detect, mode) {
  let best = { index: 0, score: 0 };
  matrix.slice(0, 30).forEach((row, index) => {
    const fields = new Set(row.filter(v => !isPrivateColumn(v)).map(v => detect(v, mode)).filter(v => v !== 'ignore'));
    const named = fields.has('full_name') || (fields.has('first_name') && fields.has('last_name'));
    const score = isSvenskalagHeader(row) ? 100 : named ? fields.size : 0;
    if (score > best.score) best = { index, score };
  });
  return { matrix: matrix.slice(best.index), headerRow: best.index + 1, detected: best.score >= 2 };
}
export function readWorkbookSheets(workbook, XLSX, detect, mode) {
  return workbook.SheetNames.map(name => {
    const sheet = workbook.Sheets[name];
    // Formatted values preserve phone-number zeroes and Excel date formatting.
    const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false, blankrows: true })
      .map(row => row.map(cleanCell));
    if (matrix[0]?.[0] === 'TRAINNOVA MALLHJÄLP – importeras inte') return null;
    const parsed = locateHeader(matrix, detect, mode);
    return { name, ...parsed, matrix: parsed.matrix.filter(row => row.some(Boolean)) };
  }).filter(sheet => sheet?.matrix.length);
}
export function parseDelimited(text) {
  const input = String(text || '').replace(/^\uFEFF/, '');
  // Count delimiters outside quotes, so commas in names do not choose the separator.
  const first = input.split(/\r?\n/).slice(0, 30).join('\n');
  const scores = ['\t', ';', ','].map(delimiter => {
    let quoted = false, count = 0;
    for (let i = 0; i < first.length; i++) {
      if (first[i] === '"') { if (quoted && first[i + 1] === '"') i++; else quoted = !quoted; }
      else if (!quoted && first[i] === delimiter) count++;
    }
    return { delimiter, count };
  }).sort((a, b) => b.count - a.count);
  const delimiter = scores[0].delimiter;
  const rows = []; let row = [], cell = '', quoted = false;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (ch === '"') {
      if (quoted && input[i + 1] === '"') { cell += '"'; i++; }
      else quoted = !quoted;
    } else if (!quoted && ch === delimiter) { row.push(cleanCell(cell)); cell = ''; }
    else if (!quoted && (ch === '\n' || ch === '\r')) {
      if (ch === '\r' && input[i + 1] === '\n') i++;
      row.push(cleanCell(cell)); if (row.some(Boolean)) rows.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (quoted) throw new Error('En cell har ett öppet citattecken. Kontrollera CSV-filen eller den inklistrade texten.');
  row.push(cleanCell(cell)); if (row.some(Boolean)) rows.push(row);
  return rows;
}
export const SAFE_HEADERS = new Set(['Förnamn', 'Efternamn', 'Medlemstyp', 'Spelare i', 'Ledare i',
  'Födelsedatum', 'Kön', 'E-post', 'E-post 1', 'Mobil', 'Telefon', 'Tröjnummer', 'Position', 'Anteckningar',
  ...[1, 2, 3].flatMap(i => [`Målsman ${i} - Namn`, `Målsman ${i} - E-post 1`, `Målsman ${i} - Mobil`])]);
// Only recognised role suffixes are stripped; parentheses in team names survive.
const SOURCE_ROLES = {ledare:'coach',coach:'coach',tränare:'coach',tranare:'coach',
  huvudledare:'head_coach',huvudtränare:'head_coach',huvudtranare:'head_coach',
  'assisterande ledare':'assistant_coach','assisterande tränare':'assistant_coach',
  'assisterande tranare':'assistant_coach','ass. tränare':'assistant_coach'};
export function sourceTeamEntries(value, mode) {
  return cleanCell(value).split(',').map(cleanCell).filter(Boolean).map(label=>{
    const match=mode==='leaders' && label.match(/^(.*\S)\s+\(([^()]+)\)$/);
    const role=match && SOURCE_ROLES[cleanCell(match[2]).toLocaleLowerCase('sv')];
    return {team:role?cleanCell(match[1]):label,role:role||'coach'};
  });
}
export function sourceLeaderRole(row, selectedTeam) {
  if (row._trainnova_role !== undefined) return row._trainnova_role;
  const roles=[...new Set(sourceTeamEntries(row['Ledare i'],'leaders')
    .filter(entry=>!selectedTeam||entry.team===selectedTeam).map(entry=>entry.role))];
  return roles.length===1?roles[0]:roles.length?'review_role':'coach';
}
// Kept in the browser. The full identifier is never part of an import payload.
export const isPersonNumberColumn = header => /^(personnr\.?|personnummer|pers\s*nr\.?)$/i.test(cleanCell(header));
export function birthDateFromPersonNumber(value, today=new Date()) {
  const raw=cleanCell(value);
  if (!/^[0-9]{8}-?[0-9]{4}$/.test(raw)) return null;
  const digits=raw.replace('-',''),year=Number(digits.slice(0,4)),month=Number(digits.slice(4,6)),day=Number(digits.slice(6,8));
  const date=new Date(Date.UTC(year,month-1,day));
  if (year<1900 || date.getUTCFullYear()!==year || date.getUTCMonth()!==month-1 || date.getUTCDate()!==day) return null;
  const iso=`${digits.slice(0,4)}-${digits.slice(4,6)}-${digits.slice(6,8)}`;
  const current=`${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  if (iso>current) return null;
  const sum=[...digits.slice(2)].reduce((total,v,i)=>{const n=Number(v)*(i%2===0?2:1);return total+Math.floor(n/10)+n%10;},0);
  return sum%10===0?iso:null;
}
export function toSvenskalagRows(matrix, {deriveBirthDates=false}={}) {
  const headers = matrix[0] || [];
  const numberColumns=headers.map((h,i)=>isPersonNumberColumn(h)?i:-1).filter(i=>i>=0);
  return matrix.slice(1).filter(row => row.some(Boolean)).map(row => {
    const safe=Object.fromEntries(
    headers.flatMap((h, i) => SAFE_HEADERS.has(cleanCell(h)) ? [[cleanCell(h), cleanCell(row[i])]] : [])
    );
    if (deriveBirthDates && safe['Spelare i'] && !safe['Födelsedatum']) {
      const values=numberColumns.map(i=>cleanCell(row[i])).filter(Boolean);
      if (values.length) {
        const dates=values.map(v=>birthDateFromPersonNumber(v));
        if (dates.every(Boolean) && new Set(dates).size===1) {
          safe['Födelsedatum']=dates[0];safe._trainnova_birth_source='personnummer';
        } else safe._trainnova_birth_issue=true;
      }
    }
    return safe;
  });
}
export function sourceTeams(rows, mode) {
  const field = mode === 'leaders' ? 'Ledare i' : 'Spelare i';
  return [...new Set(rows.flatMap(row => sourceTeamEntries(row[field],mode).map(entry=>entry.team)))];
}
export function detectExtraField(header) {
  if (isPrivateColumn(header)) return 'ignore';
  const h = normalized(header);
  if (/^(kön|kon|gender|sex)$/.test(h)) return 'gender';
  if (/^(medlemstyp|spelare i|ledare i|familj|titel|undergrupper|provtränar|trainnova importstatus|trainnova granskningsorsak)$/.test(h)) return 'ignore';
  if (/^(målsman|malsman|parent|guardian)\s*3\b/.test(h)) {
    if (/e post|epost|email|mail/.test(h)) return 'parent3_email';
    if (/mobil|telefon|phone/.test(h)) return 'parent3_phone';
    return /namn|name/.test(h) ? 'parent3_name' : 'ignore';
  }
  // The caller retains existing generic aliases for all other formats.
  return null;
}
